import {Switch} from '@headlessui/react';
import {UsersIcon} from '@heroicons/react/24/solid';
import {keyBy} from 'lodash';
import React, {useState} from 'react';
import {Tooltip} from 'react-tooltip';

import {recordEvent} from 'src/components/Analytics';
import LoadingSpinner from 'src/components/LoadingSpinner';
import {useTranslations} from 'src/components/TranslationsContext';
import {
  useAlertConfigs,
  useCreateAlertConfigMutation,
  useDeleteAlertConfigMutation,
  useUpdateAlertConfigMutation,
} from 'src/hooks/useHydroforecastAlertConfig';
import {
  useCreateNotificationPreferenceMutation,
  useNotificationPreferences,
  useUpdateNotificationPreferenceMutation,
} from 'src/hooks/useNotificationPreferences';
import {useProjectsWithSites} from 'src/hooks/useProject';
import {ApiAlertConfig} from 'src/lib/api/alert_configs';
import {ApiUserProfile} from 'src/lib/api/auth';
import {ApiNotificationPreference} from 'src/lib/api/preferences';
import {Project, Site} from 'src/lib/api/project';
import {
  HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT,
  NOTIFICATION_SCOPE_ALL,
  NOTIFICATION_SCOPE_NONE,
  NOTIFICATION_TYPE_HF_FORECAST_THRESHOLD_START,
} from 'src/lib/constants';
import * as tw from 'src/lib/tailwindClasses';

import CollapsibleNotificationsPanel from './CollapsibleNotificationPanel';
import ExpansionButton from './ExpandNotificationSectionButton';
import NewThresholdDialog from './NewThresholdDialog';

type Props = {
  currentUser: ApiUserProfile;
};

export default function ForecastThresholdNotifications({currentUser}: Props) {
  const {t} = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);
  const {notificationPreferences: prefs} = useNotificationPreferences(
    NOTIFICATION_TYPE_HF_FORECAST_THRESHOLD_START
  );
  const {alertConfigs, isLoaded: areAlertConfigsLoaded} = useAlertConfigs();

  const prefsByAlertConfigId = keyBy(prefs, function (pref) {
    return String(pref.properties.hydroforecast_alert_config_id);
  });

  const [alertConfigBeingEdited, setAlertConfigBeingEdited] = React.useState<ApiAlertConfig | null>(
    null
  );
  const [prefBeingEdited, setPrefBeingEdited] = React.useState<ApiNotificationPreference | null>(
    null
  );
  const {projects, isLoaded: areProjectsLoaded} = useProjectsWithSites();
  const [isNewAlertOpen, setIsNewAlertOpen] = React.useState<boolean>(false);
  const projectsById = keyBy(projects, 'id');
  const sitesById =
    projects &&
    keyBy(
      projects.flatMap((p) => p.sites || []),
      'id'
    );
  const updateAlertConfig = useUpdateAlertConfigMutation();
  const createAlertConfig = useCreateAlertConfigMutation();
  const deleteAlertConfig = useDeleteAlertConfigMutation();
  const updateNotificationPref = useUpdateNotificationPreferenceMutation();
  const createNotificationPref = useCreateNotificationPreferenceMutation();

  // Separate alert configs by creator
  const userAlertConfigs = alertConfigs?.filter(
    (alertConfig) => alertConfig.createdBy === currentUser.id
  );

  const otherUserAlertConfigs = alertConfigs?.filter(
    (alertConfig) => alertConfig.createdBy !== currentUser.id
  );

  const otherAlertsCount = otherUserAlertConfigs?.length || 0;

  // Enrolling in an alert is a per-user notification preference referencing the
  // alert config, so anyone in the org can enroll in alerts created by others.
  const toggleNotification = (
    alertConfig: ApiAlertConfig,
    pref: ApiNotificationPreference | undefined,
    isEnabled: boolean
  ) => {
    const scope = isEnabled ? NOTIFICATION_SCOPE_ALL : NOTIFICATION_SCOPE_NONE;
    if (pref) {
      updateNotificationPref.mutate({
        ...pref,
        scope,
      });
    } else {
      createNotificationPref.mutate({
        projectId: alertConfig.projectId,
        type: NOTIFICATION_TYPE_HF_FORECAST_THRESHOLD_START,
        scope,
        properties: {
          hydroforecast_alert_config_id: alertConfig.id,
          isEnabled: true,
        },
      });
    }
    recordEvent('notification_toggled', {
      enabled: isEnabled,
      alertConfigId: String(alertConfig.id),
    });
  };

  return (
    <div className="md:grid md:grid-cols-3 md:gap-6">
      <div className="md:col-span-1">
        <div className="px-4 sm:px-0">
          <h3 className="text-lg font-medium leading-6 text-gray-900">{t('forecastAlerts')}</h3>
          <div className="mt-1 mb-4 text-sm text-gray-600">
            <span className="text-gray-600">{t('forecastAlertDescription')}</span>{' '}
            <a
              target="_blank"
              href="https://hydroforecast.com/support/article/197-forecast-notifications/"
              rel="noreferrer"
              className={`${tw.link}`}
            >
              {t('learnMoreHere')}.
            </a>
          </div>
          <>
            {projects && isNewAlertOpen && (
              <NewThresholdDialog
                projects={projects}
                isOpen={isNewAlertOpen}
                close={() => {
                  setAlertConfigBeingEdited(null);
                  setPrefBeingEdited(null);
                  setIsNewAlertOpen(false);
                }}
                currentUser={currentUser}
                submit={(alertConfig, editAlertConfigId, pref) => {
                  let properties: ApiAlertConfig['properties'];
                  if (alertConfig.alertType === HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT) {
                    properties = {
                      source: alertConfig.source,
                      column: alertConfig.column,
                      direction: alertConfig.direction,
                      threshold: alertConfig.threshold,
                      ...(alertConfig.noise_floor_percent_lta !== undefined && {
                        noise_floor_percent_lta: alertConfig.noise_floor_percent_lta,
                      }),
                      label: alertConfig.label || undefined,
                    };
                  } else {
                    properties = {
                      source: alertConfig.source,
                      column: alertConfig.column,
                      direction: alertConfig.direction,
                      threshold: alertConfig.threshold,
                      rateVolumeMode: alertConfig.rateVolumeMode === 'level' ? 'level' : 'rate',
                      label: alertConfig.label || undefined,
                      showOnPlot: alertConfig.showOnPlot || null,
                      plotColor: alertConfig.plotColor,
                    };
                  }
                  if (editAlertConfigId) {
                    if (!pref) {
                      // Not sure that we'll ever hit this state, but
                      // just in case, create a new pref if one doesn't
                      // exist.
                      createNotificationPref.mutateAsync({
                        projectId: alertConfig.project.id,
                        type: NOTIFICATION_TYPE_HF_FORECAST_THRESHOLD_START,
                        scope: NOTIFICATION_SCOPE_ALL,
                        properties: {
                          hydroforecast_alert_config_id: editAlertConfigId,
                          isEnabled: true,
                        },
                        ccEmails: alertConfig.ccEmails,
                      });
                    } else {
                      updateNotificationPref.mutateAsync({
                        id: pref.id,
                        properties: pref.properties,
                        scope: pref.scope,
                        ccEmails: alertConfig.ccEmails,
                        type: pref.type,
                        projectId: pref.projectId,
                      });
                    }
                    return updateAlertConfig.mutateAsync({
                      id: editAlertConfigId,
                      siteIds: alertConfig.sites.map((site) => site.id),
                      properties,
                    });
                  } else {
                    return createAlertConfig.mutateAsync({
                      projectId: alertConfig.project.id,
                      siteIds: alertConfig.sites.map((site) => site.id),
                      alertType: alertConfig.alertType,
                      properties,
                      ccEmails: alertConfig.ccEmails, // For creating a new alert config, put the ccEmails on the create alert config request, so it's all in one transaction.
                    });
                  }
                }}
                editAlertConfig={alertConfigBeingEdited}
                editPref={prefBeingEdited}
              />
            )}
            <button
              className={tw.buttonPrimary}
              onClick={() => setIsNewAlertOpen(true)}
              disabled={!areProjectsLoaded || !projects || projects.length === 0}
            >
              {t('createNewAlert')}
            </button>
          </>
        </div>
      </div>
      <div className="mt-5 md:mt-0 md:col-span-2">
        <CollapsibleNotificationsPanel title={t('manageForecastAlerts')}>
          <div className="px-4 py-5 bg-white">
            <ul className="space-y-4">
              {areProjectsLoaded && areAlertConfigsLoaded ? (
                projects && projects.length > 0 ? (
                  alertConfigs && alertConfigs.length > 0 && sitesById ? (
                    <>
                      {/* User's alert configs */}
                      {userAlertConfigs
                        ?.filter((alertConfig) =>
                          alertConfig.siteIds.some((siteId) => siteId in sitesById)
                        )
                        .map((alertConfig) => {
                          const pref = prefsByAlertConfigId?.[String(alertConfig.id)];
                          return (
                            <AlertConfigRow
                              key={alertConfig.id}
                              alertConfig={alertConfig}
                              projectsById={projectsById}
                              sitesById={sitesById}
                              isCurrentUserConfig={true}
                              pref={pref}
                              onEdit={() => {
                                setAlertConfigBeingEdited(alertConfig);
                                setPrefBeingEdited(pref);
                                setIsNewAlertOpen(true);
                              }}
                              onDelete={() => {
                                if (!confirm(t('deleteConfirm'))) {
                                  return;
                                }
                                deleteAlertConfig.mutate({id: alertConfig.id});
                              }}
                              onToggleNotification={(isEnabled: boolean) =>
                                toggleNotification(alertConfig, pref, isEnabled)
                              }
                            />
                          );
                        })}

                      {/* Show other users' alert configs with expansion button */}
                      {otherAlertsCount > 0 && (
                        <>
                          {isExpanded && (
                            <>
                              <div className="mt-6 mb-2 font-bold text-gray-700 px-4">
                                {t('alertsCreatedByOthers')}
                              </div>
                              {otherUserAlertConfigs
                                ?.filter((alertConfig) =>
                                  alertConfig.siteIds.some((siteId) => siteId in sitesById)
                                )
                                .map((alertConfig) => {
                                  const pref = prefsByAlertConfigId?.[String(alertConfig.id)];
                                  return (
                                    <AlertConfigRow
                                      key={alertConfig.id}
                                      alertConfig={alertConfig}
                                      projectsById={projectsById}
                                      sitesById={sitesById}
                                      isCurrentUserConfig={false}
                                      pref={pref}
                                      onToggleNotification={(isEnabled: boolean) =>
                                        toggleNotification(alertConfig, pref, isEnabled)
                                      }
                                    />
                                  );
                                })}
                            </>
                          )}

                          <ExpansionButton
                            isExpanded={isExpanded}
                            setIsExpanded={setIsExpanded}
                            expandText={t('showAlertsCreatedByOthers')}
                            collapseText={t('hideAlertsCreatedByOthers')}
                            count={otherAlertsCount}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <p>{t('noAlertsMessage', {createNewAlert: t('createNewAlert')})}</p>
                  )
                ) : (
                  <p>{t('noProjectsMessage')}</p>
                )
              ) : (
                <div className="flex justify-center">
                  <LoadingSpinner />
                </div>
              )}
            </ul>
          </div>
        </CollapsibleNotificationsPanel>
      </div>
    </div>
  );
}

// Shared AlertConfigRow component
type AlertConfigRowProps = {
  alertConfig: ApiAlertConfig;
  projectsById: Record<string, Project>;
  sitesById: Record<string, Site>;
  isCurrentUserConfig: boolean;
  pref?: ApiNotificationPreference;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleNotification?: (isEnabled: boolean) => void;
};

function AlertConfigRow({
  alertConfig,
  projectsById,
  sitesById,
  isCurrentUserConfig,
  pref,
  onEdit,
  onDelete,
  onToggleNotification,
}: AlertConfigRowProps) {
  const {t} = useTranslations();
  const ccEmails = pref?.ccEmails || [];
  const hasCcEmails = ccEmails.length > 0;

  return (
    <li className="flex items-center py-3 px-4">
      <div className="flex-1 border-r pr-4 mr-4">
        <div>
          <p className="truncate text-sm font-bold">{projectsById[alertConfig.projectId].name}</p>
          <p className="mt-1 flex items-center text-sm text-gray-500">
            <span>
              {sitesById[alertConfig.siteIds[0]]?.name}
              {alertConfig.siteIds.length > 1 && (
                <>
                  <span
                    data-tooltip-id={`sites-tooltip-${alertConfig.id}`}
                    className="italic font-semibold text-slate-500"
                  >
                    {' '}
                    {t('and')} {alertConfig.siteIds.length - 1} {t('more').toLowerCase()}
                  </span>
                  <Tooltip id={`sites-tooltip-${alertConfig.id}`} className="z-50">
                    {alertConfig.siteIds.slice(1).map((siteId) => (
                      <div key={siteId}>{sitesById[siteId]?.name}</div>
                    ))}
                  </Tooltip>
                </>
              )}
              {alertConfig.properties.label && (
                <span className="text-gray-700 font-medium">, {alertConfig.properties.label}</span>
              )}
            </span>
          </p>
        </div>
        <div className="mt-2 flex flex-wrap justify-between items-center">
          <p className="text-sm text-gray-500">
            <code>
              {alertConfig.properties.source} {alertConfig.properties.column}{' '}
              {'rateVolumeMode' in alertConfig.properties
                ? alertConfig.properties.rateVolumeMode || 'rate'
                : ''}
            </code>
          </p>
          <div className="flex items-center">
            {hasCcEmails && (
              <span
                data-tooltip-id={`cc-emails-tooltip-${alertConfig.id}`}
                className="mr-2 inline-flex items-center"
              >
                <UsersIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                <Tooltip id={`cc-emails-tooltip-${alertConfig.id}`} className="z-50">
                  <div className="text-left">
                    <div className="font-semibold mb-1">{t('recipients')}:</div>
                    {ccEmails.map((email) => (
                      <div key={email}>{email}</div>
                    ))}
                  </div>
                </Tooltip>
              </span>
            )}
            <p className="rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
              {alertConfig.alertType === HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT ? (
                <span>{t('upcomingEvent')}</span>
              ) : (
                <>
                  {alertConfig.properties.direction === 'above' ? '>' : '<'}{' '}
                  {alertConfig.properties.threshold} {alertConfig.units}
                </>
              )}
            </p>
            {'showOnPlot' in alertConfig.properties && alertConfig.properties.showOnPlot && (
              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 text-xs font-semibold leading-5 text-gray-700">
                {'plotColor' in alertConfig.properties && alertConfig.properties.plotColor && (
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{backgroundColor: alertConfig.properties.plotColor}}
                    aria-hidden="true"
                  />
                )}
                {alertConfig.properties.showOnPlot === 'everyone' ? t('plotAll') : t('plotMe')}
              </span>
            )}
          </div>
        </div>
        {!isCurrentUserConfig && (
          <div className="mt-2 flex flex-wrap justify-between items-center">
            <p className="text-sm text-gray-500">
              {t('createdBy', {name: alertConfig.createdByName})}
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col text-center space-y-2">
        <Switch
          checked={pref ? pref.scope === NOTIFICATION_SCOPE_ALL : false}
          onChange={onToggleNotification}
          className={`${
            pref?.scope === NOTIFICATION_SCOPE_ALL ? 'bg-blue-600' : 'bg-gray-200'
          } relative inline-flex h-6 w-11 items-center rounded-full`}
        >
          <span className="sr-only">{t('enableThresholdNotification')}</span>
          <span
            className={`${
              pref?.scope === NOTIFICATION_SCOPE_ALL ? 'translate-x-6' : 'translate-x-1'
            } inline-block h-4 w-4 transform rounded-full bg-white transition`}
          />
        </Switch>

        {isCurrentUserConfig && (
          <>
            <button className="text-sm text-gray-500" onClick={onEdit}>
              {t('edit')}
            </button>
            <button className="text-sm text-red-500" onClick={onDelete}>
              {t('delete')}
            </button>
          </>
        )}
      </div>
    </li>
  );
}
